import {
  Box,
  Button,
  Checkbox,
  ConfirmationDialogBox,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableFoot as TableFooter,
  TableHead,
  TableHeaderCell,
  TableRow,
  NumericInput,
  TextField,
  Typography,
} from '@atgfr8/atg-ui-tailwind';
import {
  ContentCopyIcon,
  CurrencyUsdIcon,
  DeleteIcon,
  PlusCircleOutlineIcon,
} from '@atgfr8/icons';
import {
  BasicModal,
  SelectInputSearchable,
  TableRowErrors,
  ToastType,
} from '@components';
import { CARRIER_TOTAL_EXCEEDS_MAX_RATE_BOOKED_MESSAGE } from '@common/order/constants';
import { computeCarrierLineItemsTotal } from '@common/order/validations';
import { EmptyData } from '@components/EmptyData/EmptyData';
import { HoverPop } from '@components/HoverPop';
import Notification from '@components/Notification/Notification';
import tableErrorClasses from '@components/TableErrorsRow/classes';
import {
  checkIfDialogBoxIsOpen,
  closeDialogBox,
  openDialogBox,
} from '@store/dialogBox';
import { FeatureFlags } from '@constants/featureFlags';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { openModal } from '@store/modals';
import { getOrderCarrierMaxRateBlock, isCreate } from '@store/orders/selectors';
import {
  carrierStore,
  orderStore,
  setOrderStore,
  updateLoadPropertyAtIndex,
  validateFieldForOrder,
} from '@store/orders';
import {
  LineItemViewModel,
  OrderLineItem,
} from '@typeDefinitions/lineItemTypes';
import { ILoadEditViewModel } from '@typeDefinitions/loadTypes';
import { OperationTypeV4 } from '@typeDefinitions/operationTypes';
import {
  formatAmount,
  handleToast,
  isAdmin,
  isSuperAdmin,
  orderTableErrors,
} from '@utils/utils';
import {
  DELETE_CARRIER_PAY_ROW_DIALOG_ID,
  fuelCharges,
  lineItemsTypesForCarrierPayNew,
} from '@views/order/constants';
import { Accessor, Index, Show, createEffect, createSignal } from 'solid-js';

import { LineItemsLockedModel } from '../models';
import { EFSModal } from './EFSModal';
import classes from './classes';
import {
  isManager,
  removeTableRow,
  userHasPermission,
} from './stops/stopsEditor/utilFunctions';

type Props = {
  load: ILoadEditViewModel;
  tabIndex: number;
};

export const CarrierPay = (props: Props) => {
  const [deleteindex, setDeleteindex] = createSignal<number | null>(null);
  const [selectedLineItem, setSelectedLineItem] =
    createSignal<LineItemViewModel | null>(null);

  createEffect(() => {
    if (
      orderStore.isCreate &&
      orderStore.order.loads[orderStore.activeTab.index].lineItems === undefined
    ) {
      addRow();
      onLineItemsChange('type', 'Line Haul', 0);
    }
  });

  const editLineItems = `editLineItems-${props.tabIndex}`;
  const efsModalId = 'EFSModal';

  const addRow = () => {
    if (orderStore.isReadOnly) {
      return;
    }

    const lineItems = props.load.lineItems ? [...props.load.lineItems] : [];
    lineItems.push({
      operationType: 'Insert',
      quantity: 1,
      type: '',
      description: '',
      rate: 0,
      estimate: false,
      //check later
      id: 0,
      readOnly: false,
      synched: false,
      adjustment: false,
      stopId: 0,
      status: 'Unsettled',
      pickUpId: undefined,
      dropOffId: undefined,
    });
    updateLoadPropertyAtIndex({ lineItems: lineItems });
  };

  const validateMaxRate = () => {
    void validateFieldForOrder(
      `loads.${props.tabIndex}.maxRateError`,
      `loads[${props.tabIndex}].maxRateError`,
      { load: props.load },
    );
  };

  const validateRowField = (
    index: number,
    field: 'type' | 'quantity' | 'rate',
  ) => {
    const path = `loads.${props.tabIndex}.lineItems.${index}.${field}`;
    const errorPath = `loads[${props.tabIndex}].lineItems[${index}].${field}`;

    void validateFieldForOrder(path, errorPath, { load: props.load });

    if (field === 'rate' || field === 'quantity') {
      validateMaxRate();
    }
  };

  const maxRateBlock = () => getOrderCarrierMaxRateBlock(props.load);

  const isSubmitBidAvailable = () =>
    isFeatureFlagEnabled(FeatureFlags.OfferDetails)() &&
    isFeatureFlagEnabled(FeatureFlags.MyOffers)() &&
    !isCreate();

  const scrollToSubmitBid = () => {
    document.getElementById('submit-new-bid')?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  };

  const onLineItemsChange = (
    key: string,
    value: string | number | boolean,
    index: number,
  ) => {
    const lineItems = props.load.lineItems ? [...props.load.lineItems] : [];
    let opType = 'Insert' as OperationTypeV4;
    if (lineItems.length === 0) {
      return;
    }
    if (lineItems[index].id > 0) {
      opType = 'Update' as OperationTypeV4;
    }
    lineItems[index] = {
      ...lineItems[index],
      [key]: value,
      operationType: opType,
    };
    updateLoadPropertyAtIndex({ lineItems: lineItems });
  };

  const headers = [
    'Type',
    'Description',
    'Qty',
    'Rate',
    'EFS',
    'Est?',
    'Total',
  ];

  const cellStyles = {
    padding: '2px 6px',
    'border-bottom': '0px',
  };

  const getRowBorderCellClass = (index: number) =>
    index === 0 ? 'border-b-0!' : 'border-t! border-b-0! border-[#a8c5bf]!';

  const computeTotalLineItems = () =>
    computeCarrierLineItemsTotal(props.load.lineItems);

  const getEfsRequestStyling = (lineItemId: number, disabled = false) => {
    const lineItem = orderStore.order.loads[
      orderStore.activeTab.index
    ].lineItems?.find((obj) => obj.id === lineItemId);
    let classString = '';
    if (lineItem?.status === 'Check Approved') {
      classString = classes.dollarIconApproved;
    }
    if (lineItem?.status === 'Requested') {
      classString = classes.dollarIconRequested;
    }
    if (
      lineItem?.status === 'Unsettled' ||
      lineItem?.status === 'Pending Sync'
    ) {
      classString = classes.dollarIcon;
    }
    if (lineItem?.readOnly ?? false) {
      classString = classString + ' cursor-not-allowed';
    }
    return `${classString} ${disabled ? 'bg-slate-400!' : ''}`;
  };

  const UserCanEFS = () => {
    return (
      isManager() ||
      isAdmin() ||
      userHasPermission('Approve Checks') ||
      userHasPermission('Request EFS Checks')
    );
  };

  const requestCheck = (lineItem: LineItemViewModel) => {
    const isFuelAdvance = fuelCharges.indexOf(lineItem.type!) != -1;
    const validStatuses = [
      'Loaded',
      'In Transit',
      'At Shipper',
      'Out On Delivery',
      'At Receiver',
    ];

    if (!Boolean(orderStore.order.loads[orderStore.activeTab.index].carrierId))
      handleToast(ToastType.Error, 'There is no carrier attached to this load');
    else if (
      !carrierStore.carrier[orderStore.activeTab.index].advancesAllowed &&
      isFuelAdvance
    )
      handleToast(
        ToastType.Caution,
        'This Carrier cannot receive cash advances',
      );
    else if (lineItem.status == 'Requested')
      handleToast(ToastType.Caution, 'There is already a pending request out');
    else if (lineItem.status == 'Check Approved')
      handleToast(
        ToastType.Caution,
        'You can only request one check per line item',
      );

    if (
      validStatuses.indexOf(
        orderStore.order.loads[orderStore.activeTab.index].status ?? '',
      ) == -1
    ) {
      handleToast(
        ToastType.Caution,
        'Status has to be Loaded, At Shipper, In Transit, Out On Delivery, or At Receiver to request check.',
      );
    } else {
      setSelectedLineItem(lineItem);
      openModal(efsModalId);
    }
  };

  return (
    <>
      <div>
        <Table>
          <TableHead>
            <TableRow>
              {headers.map((item) => (
                <TableHeaderCell
                  class="text-black font-medium text-[12px]"
                  style={{ padding: '4px 8px' }}
                >
                  {item}
                </TableHeaderCell>
              ))}
            </TableRow>
          </TableHead>
          <Show
            when={
              props.load.lineItems?.filter(
                (item) => item.operationType !== 'Delete',
              ).length === 0
            }
          >
            <TableBody class="w-full h-64 bg-[#BFDFD8]">
              <TableRow>
                <TableCell colSpan={headers.length + 1} style={{ border: 0 }}>
                  <EmptyData />
                </TableCell>
              </TableRow>
            </TableBody>
          </Show>
          <Show when={props.load.lineItems && props.load.lineItems.length > 0}>
            <TableBody class={classes.tableBg}>
              <Index each={props.load.lineItems ?? []}>
                {(item: Accessor<LineItemViewModel>, index) => (
                  <Show when={item().operationType !== 'Delete'}>
                    <TableRow
                      class={`${classes.tableRow} ${
                        item().readOnly
                          ? 'cursor-not-allowed'
                          : 'cursor-default'
                      }`}
                    >
                      <TableCell
                        class={getRowBorderCellClass(index)}
                        style={{ ...cellStyles, 'max-width': '250px' }}
                      >
                        <SelectInputSearchable
                          dataTestId={`type-select-${index}`}
                          label=""
                          placeholder=""
                          size="md"
                          value={item().type as string}
                          menuItems={lineItemsTypesForCarrierPayNew}
                          onChange={(value: string) => {
                            onLineItemsChange('type', value, index);
                          }}
                          onBlur={() => validateRowField(index, 'type')}
                          disabled={item().readOnly || orderStore.isReadOnly}
                        />
                      </TableCell>
                      <TableCell
                        class={getRowBorderCellClass(index)}
                        style={{ ...cellStyles, 'min-width': '130px' }}
                      >
                        <TextField
                          value={item().description as string}
                          onChange={(value: string) => {
                            onLineItemsChange(
                              'description',
                              String(value),
                              index,
                            );
                          }}
                          class={'bg-white rounded-sm h-[40px]!'}
                          label=""
                          placeholder="Description"
                          size="sm"
                          disabled={
                            item().readOnly ||
                            item().type === '' ||
                            orderStore.isReadOnly
                          }
                          dataTestId="description-textInput"
                        />
                      </TableCell>
                      <TableCell
                        class={getRowBorderCellClass(index)}
                        style={{
                          ...cellStyles,
                          'min-width': '50px',
                          'max-width': '60px',
                        }}
                      >
                        <NumericInput
                          label=""
                          value={String(item().quantity)}
                          onChange={(value: string) => {
                            onLineItemsChange('quantity', value, index);
                          }}
                          on:blur={() => validateRowField(index, 'quantity')}
                          error={Boolean(
                            orderStore.orderFormError?.[
                              `loads[${props.tabIndex}].lineItems[${index}].quantity`
                            ],
                          )}
                          placeholder="Qty"
                          class={'bg-white rounded-sm h-[40px]!'}
                          disabled={item().readOnly || orderStore.isReadOnly}
                          size="sm"
                          dataTestId="quantity-textInput"
                        />
                      </TableCell>
                      <TableCell
                        class={getRowBorderCellClass(index)}
                        style={{
                          ...cellStyles,
                          'min-width': '70px',
                          'max-width': '115px',
                        }}
                      >
                        <NumericInput
                          value={String(item().rate ?? '')}
                          onChange={(value: string) => {
                            onLineItemsChange('rate', value, index);
                          }}
                          dataTestId={'rate-textInput'}
                          on:blur={() => validateRowField(index, 'rate')}
                          error={Boolean(
                            orderStore.orderFormError?.[
                              `loads[${props.tabIndex}].lineItems[${index}].rate`
                            ],
                          )}
                          label=""
                          placeholder="Rate"
                          class={'bg-white rounded-sm h-[40px]!'}
                          disabled={item().readOnly || orderStore.isReadOnly}
                          size="sm"
                        />
                      </TableCell>
                      <Show when={UserCanEFS()}>
                        <TableCell
                          style={{ ...cellStyles, padding: '2px 6px' }}
                          class={`text-center! ${getRowBorderCellClass(index)}`}
                        >
                          <Show when={item().id > 0}>
                            <IconButton
                              disabled={
                                item().readOnly ||
                                (orderStore.order.loads[props.tabIndex]
                                  .lineItems?.[index].operationType ??
                                  'None') !== 'None'
                              }
                              onClick={() => requestCheck(item())}
                              dataTestId={`request-efs-check-button-${item().id}`}
                            >
                              <CurrencyUsdIcon
                                class={getEfsRequestStyling(
                                  item().id,
                                  (orderStore.order.loads[props.tabIndex]
                                    .lineItems?.[index].operationType ??
                                    'None') !== 'None',
                                )}
                              />
                            </IconButton>
                          </Show>
                        </TableCell>
                      </Show>
                      <TableCell class={getRowBorderCellClass(index)}>
                        <div class="flex justify-center">
                          <Checkbox
                            checked={
                              props.load.lineItems &&
                              props.load.lineItems[index]['estimate']
                            }
                            onChange={(value) => {
                              onLineItemsChange('estimate', value, index);
                            }}
                            disabled={item().readOnly || orderStore.isReadOnly}
                            dataTestId="estimate-checkboxInput"
                            size="xs"
                            color="info"
                            class="ml-[-9px]"
                          />
                        </div>
                      </TableCell>
                      <TableCell class={getRowBorderCellClass(index)}>
                        <Typography
                          variant="body1"
                          class={`${classes.totalAmount} font-normal!`}
                        >
                          {formatAmount(
                            Number(item().quantity) * Number(item().rate),
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell
                        class={getRowBorderCellClass(index)}
                        style={{ padding: '2px 6px' }}
                      >
                        <div class="flex items-center gap-2">
                          <HoverPop
                            anchor={
                              <ContentCopyIcon
                                class="text-[#45545b]! cursor-pointer"
                                onClick={() => {
                                  setOrderStore(
                                    'order',
                                    'lineItems',
                                    (lineItems) => [
                                      ...lineItems,
                                      {
                                        ...item(),
                                        stopId: undefined,
                                        operationType: 'Insert',
                                        synched: false,
                                        settledDate: null,
                                        createdDate: null,
                                        referenceNumber: null,
                                      } as OrderLineItem,
                                    ],
                                  );
                                }}
                              />
                            }
                            render={true}
                            marginTop="8px"
                          >
                            <div class="text-sm p-2">
                              Copy line item to{' '}
                              <span class="font-bold">Customer Pay</span>
                            </div>
                          </HoverPop>
                          <IconButton
                            disabled={item().readOnly || orderStore.isReadOnly}
                            onClick={() => {
                              setDeleteindex(index);
                              openDialogBox(DELETE_CARRIER_PAY_ROW_DIALOG_ID);
                            }}
                            dataTestId={`delete-carrier-line-item-button-${index}`}
                          >
                            <DeleteIcon class={classes.deleteIcon} />
                          </IconButton>
                        </div>
                      </TableCell>
                    </TableRow>
                    <TableRowErrors
                      columnsLength={headers.length}
                      tableErrors={orderTableErrors(
                        ['rate', 'quantity', 'type', 'description'],
                        `loads[${props.tabIndex}].lineItems[{index}].{field}`,
                        index,
                      )}
                    />
                  </Show>
                )}
              </Index>
              <Show when={maxRateBlock()}>
                {(block) => (
                  <TableRow class={tableErrorClasses.tableRow}>
                    <TableCell
                      colSpan={headers.length + 1}
                      class={tableErrorClasses.errorRow}
                    >
                      <Notification
                        type="tableError"
                        tableRowNotification
                        text={
                          <span
                            class="text-base text-black font-normal"
                            data-testid="carrier-max-rate-error"
                          >
                            <Show
                              when={block() === 'booking'}
                              fallback={
                                CARRIER_TOTAL_EXCEEDS_MAX_RATE_BOOKED_MESSAGE
                              }
                            >
                              Cannot save carrier charges when carrier total is
                              greater than max rate. Please{' '}
                              <Show
                                when={isSubmitBidAvailable()}
                                fallback="submit"
                              >
                                <Button
                                  variant="text"
                                  size="sm"
                                  disableRipple
                                  class="inline! p-0! min-w-0! h-auto! align-baseline! normal-case! text-base! font-normal! underline! text-armstrong-blue!"
                                  onClick={scrollToSubmitBid}
                                  dataTestId="carrier-max-rate-submit-link"
                                >
                                  submit
                                </Button>
                              </Show>{' '}
                              offer for approval.
                            </Show>
                          </span>
                        }
                      />
                    </TableCell>
                  </TableRow>
                )}
              </Show>
            </TableBody>
          </Show>
          <TableFooter class={classes.tableFooter}>
            <TableRow class={classes.tableFooterRow}>
              <TableCell class="border-t! border-[#a8c5bf]!" colSpan={1}>
                <IconButton
                  class="p-0!"
                  onClick={addRow}
                  disabled={
                    (orderStore.order.loads[orderStore.activeTab.index]
                      ?.status === 'Paid' ||
                      orderStore.order.loads[orderStore.activeTab.index]
                        ?.status === 'Invoiced') &&
                    (!isAdmin() || !isSuperAdmin())
                  }
                  dataTestId="add-carrier-line-item-button"
                >
                  <PlusCircleOutlineIcon class={classes.addIcon} />
                </IconButton>
              </TableCell>
              <TableCell
                class="border-t! border-[#a8c5bf]!"
                colSpan={4}
              ></TableCell>
              <TableCell class="border-t! border-[#a8c5bf]!" colSpan={3}>
                <div class="flex items-center gap-2">
                  <div class="font-medium text-sm text-black">
                    Carrier Total
                  </div>
                  <div class="font-medium text-sm text-black">
                    {formatAmount(computeTotalLineItems())}
                  </div>
                </div>
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </div>
      <Show when={props.load.synched}>
        <Box class="mt-2">
          <span
            class={classes.editLineItems}
            onClick={() => openModal(editLineItems)}
          >
            Why can't I edit my Line Items?
          </span>
        </Box>
      </Show>
      <BasicModal
        id={editLineItems}
        width={'800px'}
        footer={false}
        header={false}
      >
        <LineItemsLockedModel />
      </BasicModal>
      <BasicModal id={efsModalId} footer={false} header={false} width={'750px'}>
        <Show when={selectedLineItem()}>
          <EFSModal
            lineItem={selectedLineItem()!}
            firstLoadWarning={carrierStore.carrier[props.tabIndex].firstLoad}
          />
        </Show>
      </BasicModal>
      <ConfirmationDialogBox
        isDialogOpen={checkIfDialogBoxIsOpen(DELETE_CARRIER_PAY_ROW_DIALOG_ID)}
        dataTestId={DELETE_CARRIER_PAY_ROW_DIALOG_ID}
        id={DELETE_CARRIER_PAY_ROW_DIALOG_ID}
        title="Are you sure you want to delete this row?"
        onSubmit={() => {
          removeTableRow(deleteindex()!, props.load, 'load', 'lineItems');
          setDeleteindex(null);
          validateMaxRate();
        }}
        onSubmitText="Delete"
        closeDialogBox={(_reason) =>
          closeDialogBox(DELETE_CARRIER_PAY_ROW_DIALOG_ID)
        }
      />
    </>
  );
};
